/**
 * CoConnect AI Copilot Widget
 * ───────────────────────────
 * A floating chat button that appears on every portal. Gives all users
 * access to the COPILOT agent (SVC-033) + knowledge search.
 * Also provides quick-access to specialized agents based on user role.
 *
 * v3.2.6 Enterprise-grade upgrades:
 * - Auto-routing: when COPILOT suggests a specialist agent, automatically
 *   routes to it and shows "Routing to {agent}..." status, then "Done"
 *   when the specialist responds. User never wonders what's happening.
 * - Voice input (STT): microphone button uses Web Speech API (browser-native,
 *   no key needed) or Groq Whisper (if configured) for EN/FR/Pidgin/local.
 * - Voice output (TTS): speaker button on each AI response reads it aloud
 *   using Web Speech API (browser-native) or Fish Audio (if configured).
 * - Language selector: EN / FR / Pidgin / Mokpe / Oroko / Fulfulde.
 * - Streaming feel: shows "Thinking..." → "Routing..." → "Done" status.
 */
import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { apiClient } from '../services/api';

interface CopilotMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: string;
  agentCode?: string;
  confidence?: number;
  sources?: any[];
  isStatus?: boolean; // v3.2.6: status messages (routing, thinking, done)
  spoken?: boolean; // v3.2.6: tracks if TTS was triggered for this message
}

// v3.2.6: Cameroon language options for STT/TTS
const LANGUAGES = [
  { code: 'en-US', label: 'English', flag: '🇬🇧' },
  { code: 'fr-FR', label: 'Français', flag: '🇫🇷' },
  { code: 'pcm', label: 'Pidgin', flag: '🗣️' },
  { code: 'mok', label: 'Mokpe', flag: '🟢' },
  { code: 'oroko', label: 'Oroko', flag: '🟢' },
  { code: 'fuv', label: 'Fulfulde', flag: '🟢' },
];


// v3.11.49: Enterprise-grade SVG icons (mature, no cartoony emojis)
const MicIcon = () => (<svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" /><path d="M19 10v2a7 7 0 01-14 0v-2" /><line x1="12" y1="19" x2="12" y2="23" /><line x1="8" y1="23" x2="16" y2="23" /></svg>);
const MicOffIcon = () => (<svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><line x1="1" y1="1" x2="23" y2="23" /><path d="M9 9v3a3 3 0 005.12 2.12M15 9.34V4a3 3 0 00-5.94-.6" /><path d="M17 16.95A7 7 0 015 12v-2m14 0v2a7 7 0 01-.11 1.23" /><line x1="12" y1="19" x2="12" y2="23" /><line x1="8" y1="23" x2="16" y2="23" /></svg>);
const SendIcon = () => (<svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></svg>);
const SpeakerIcon = () => (<svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" /><path d="M15.54 8.46a5 5 0 010 7.07" /><path d="M19.07 4.93a10 10 0 010 14.14" /></svg>);
const SpeakerOffIcon = () => (<svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" /><line x1="23" y1="9" x2="17" y2="15" /><line x1="17" y1="9" x2="23" y2="15" /></svg>);
const BookIcon = () => (<svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z" /><path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z" /></svg>);
const GlobeIcon = () => (<svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" /><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" /></svg>);
const SparkIcon = ({ className = 'w-5 h-5' }: { className?: string }) => (<svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 3l1.35 4.15L17.5 8.5l-4.15 1.35L12 14l-1.35-4.15L6.5 8.5l4.15-1.35L12 3z"/><path d="M18.5 14.5l.72 2.28 2.28.72-2.28.72-.72 2.28-.72-2.28-2.28-.72 2.28-.72.72-2.28z"/><path d="M5 14l.55 1.7 1.7.55-1.7.55L5 18.5l-.55-1.7-1.7-.55 1.7-.55L5 14z"/></svg>);
const RouteIcon = ({ className = 'w-4 h-4' }: { className?: string }) => (<svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="6" cy="18" r="2"/><circle cx="18" cy="6" r="2"/><path d="M8 18h3a3 3 0 003-3V9a3 3 0 013-3h-1"/></svg>);
const ChevronDownIcon = () => (<svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6"/></svg>);


export const CopilotWidget: React.FC<{ userRole?: string; workspaceContext?: string }> = ({ userRole = 'admin', workspaceContext = '' }) => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<CopilotMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeAgent, setActiveAgent] = useState('COPILOT');
  const [showKnowledge, setShowKnowledge] = useState(false);
  const [knowledgeResults, setKnowledgeResults] = useState<any[]>([]);
  const [language, setLanguage] = useState('en'); // v3.11.49: unified // v3.2.6: STT/TTS language
  const [isListening, setIsListening] = useState(false); // v3.2.6: STT active
  const [speakingMessageIdx, setSpeakingMessageIdx] = useState<number | null>(null); // v3.2.6: TTS active
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesScrollRef = useRef<HTMLDivElement>(null);
  const recognitionRef = useRef<any>(null); // v3.2.6: Web Speech API ref
  const panelRef = useRef<HTMLDivElement>(null);
  const fabRef = useRef<HTMLButtonElement>(null);
  const isDriver = userRole === 'driver';
  const isFieldAgent = userRole === 'field_agent';
  const isFarmer = userRole === 'farmer';
  const isAdmin = userRole === 'admin';
  const isFactoryClerk = userRole === 'factory_clerk';
  const isFinance = userRole === 'finance';
  const isOperationalMobileCopilot = isDriver || isFieldAgent || isFarmer;

  // Role-based agent suggestions
  // v3.2 Fix Group D: expanded per spec — every role now has access to the
  // full set of agents the spec mandates. Removed the bogus `QUALITY` button
  // (no such agent in the registry — quality grading is a separate route).
  const roleAgents: Record<string, Array<{ code: string; label: string; icon: string }>> = {
    admin: [
      { code: 'COPILOT', label: 'Copilot', icon: '💬' },
      { code: 'EXECUTIVE', label: 'Executive', icon: '📊' },
      { code: 'DISEASE', label: 'Disease', icon: '🔬' },
      { code: 'KNOWLEDGE', label: 'Knowledge', icon: '<BookIcon />' },
      { code: 'RISK', label: 'Risk', icon: '⚠️' },
      { code: 'COMPLIANCE', label: 'EUDR Compliance', icon: '🌍' }, // v3.2
      { code: 'MARKET', label: 'Market', icon: '📈' }, // v3.2
      { code: 'TRANSLATION', label: 'Translation', icon: '🌐' }, // v3.2
    ],
    farmer: [
      { code: 'COPILOT', label: 'Copilot', icon: '💬' },
      { code: 'DISEASE', label: 'Disease Diagnosis', icon: '🔬' },
      { code: 'PEST', label: 'Pest ID', icon: '🐛' }, // v3.2
      { code: 'YIELD', label: 'Yield Prediction', icon: '🌱' },
      { code: 'FERTILIZER', label: 'Fertilizer', icon: '🧪' },
      { code: 'AGRONOMIST', label: 'Agronomist', icon: '🌾' }, // v3.2
      { code: 'WEATHER_INTEL', label: 'Weather', icon: '🌤️' },
      { code: 'HARVEST', label: 'Harvest', icon: '🫘' }, // v3.2
      { code: 'KNOWLEDGE', label: 'Knowledge', icon: '<BookIcon />' },
      { code: 'TRANSLATION', label: 'Translation', icon: '🌐' }, // v3.2
    ],
    field_agent: [
      { code: 'COPILOT', label: 'Copilot', icon: '💬' },
      { code: 'DISEASE', label: 'Disease', icon: '🔬' },
      { code: 'PEST', label: 'Pest ID', icon: '🐛' },
      { code: 'SOIL', label: 'Soil', icon: '🪨' },
      { code: 'FERTILIZER', label: 'Fertilizer', icon: '🧪' }, // v3.2
      { code: 'YIELD', label: 'Yield', icon: '🌱' }, // v3.2
      { code: 'AGRONOMIST', label: 'Agronomist', icon: '🌾' }, // v3.2
      { code: 'EXTENSION', label: 'Extension', icon: '🎓' }, // v3.2
      { code: 'HARVEST', label: 'Harvest', icon: '🫘' }, // v3.2
      { code: 'KNOWLEDGE', label: 'Knowledge', icon: '<BookIcon />' },
      { code: 'TRANSLATION', label: 'Translation', icon: '🌐' }, // v3.2
    ],
    finance: [
      { code: 'COPILOT', label: 'Copilot', icon: 'assistant' },
      { code: 'MARKET', label: 'Market', icon: 'market' },
      { code: 'FINANCE', label: 'Finance', icon: 'finance' },
      { code: 'EXECUTIVE', label: 'Executive', icon: 'executive' },
      { code: 'RISK', label: 'Risk', icon: 'risk' }, // v3.2
      { code: 'COMPLIANCE', label: 'EUDR Compliance', icon: 'compliance' }, // v3.2
      { code: 'KNOWLEDGE', label: 'Knowledge', icon: 'knowledge' }, // v3.2
      { code: 'TRANSLATION', label: 'Translation', icon: 'translation' }, // v3.2
    ],
    factory_clerk: [
      { code: 'COPILOT', label: 'Copilot', icon: 'assistant' },
      { code: 'WAREHOUSE', label: 'Warehouse', icon: 'warehouse' },
      { code: 'LOGISTICS', label: 'Logistics', icon: 'route' }, // v3.2: replaces bogus QUALITY
      { code: 'KNOWLEDGE', label: 'Knowledge', icon: 'knowledge' }, // v3.2
    ],
    driver: [
      { code: 'COPILOT', label: 'Copilot', icon: '💬' },
      { code: 'LOGISTICS', label: 'Logistics', icon: '🚛' },
    ],
    dpo: [ // v3.2: new role — DPO has access to COPILOT only (per spec)
      { code: 'COPILOT', label: 'Copilot', icon: '💬' },
    ],
    warehouse_manager: [ // v3.2: alias for factory_clerk (warehouse_manager role
      // is mentioned in spec but doesn't exist in the codebase — using same set)
      { code: 'COPILOT', label: 'Copilot', icon: '💬' },
      { code: 'WAREHOUSE', label: 'Warehouse', icon: '🏭' },
      { code: 'LOGISTICS', label: 'Logistics', icon: '🚛' },
      { code: 'KNOWLEDGE', label: 'Knowledge', icon: '<BookIcon />' },
    ],
  };

  const agents = roleAgents[userRole] || roleAgents.admin;

  useEffect(() => {
    if (isOperationalMobileCopilot) {
      const scroller = messagesScrollRef.current;
      if (!scroller) return;
      const distanceFromBottom = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight;
      if (distanceFromBottom > 120) return;
      scroller.scrollTo({ top: scroller.scrollHeight, behavior: 'smooth' });
      return;
    }
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isOperationalMobileCopilot]);

  // Driver mobile presentation only: make the open Copilot a top-level application
  // surface, lock the Truck UI behind it, and contain keyboard focus.
  useEffect(() => {
    if (!isOperationalMobileCopilot || !open || typeof document === 'undefined') return;
    const previousOverflow = document.body.style.overflow;
    document.body.classList.add('copilot-sheet-open');
    document.body.style.overflow = 'hidden';
    const panel = panelRef.current;
    const focusableSelector = 'button:not([disabled]), select:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])';
    const initial = panel?.querySelector<HTMLElement>(focusableSelector);
    window.setTimeout(() => initial?.focus(), 0);

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        return;
      }
      if (event.key !== 'Tab' || !panel) return;
      const focusable = Array.from(panel.querySelectorAll<HTMLElement>(focusableSelector)).filter(el => !el.hasAttribute('disabled'));
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.classList.remove('copilot-sheet-open');
      document.body.style.overflow = previousOverflow;
      window.setTimeout(() => fabRef.current?.focus(), 0);
    };
  }, [isOperationalMobileCopilot, open]);

  const operationalStatusText = (content: string) => isOperationalMobileCopilot
    ? content.replace(/^[🤔🔧🔄✅💡⚠️]\s*/u, '').replace(' specialist is handling your question...', ' is handling your question...')
    : content;

  const driverStatusText = operationalStatusText;

  const sendMessage = async (overrideAgent?: string) => {
    if (!input.trim() || loading) return;

    const targetAgent = overrideAgent || activeAgent;
    const userMessage: CopilotMessage = {
      role: 'user',
      content: input,
      timestamp: new Date().toISOString(),
    };
    setMessages(prev => [...prev, userMessage]);
    const currentInput = input;
    setInput('');
    setLoading(true);

    // v3.2.6: Show "Thinking..." status immediately for enterprise-grade UX
    if (targetAgent === 'COPILOT') {
      setMessages(prev => [...prev, {
        role: 'system',
        content: '🤔 Thinking...',
        timestamp: new Date().toISOString(),
        isStatus: true,
      }]);
    } else {
      setMessages(prev => [...prev, {
        role: 'system',
        content: `🔧 Routing to ${targetAgent} specialist...`,
        timestamp: new Date().toISOString(),
        isStatus: true,
      }]);
    }

    try {
      // Call the AI agent
      const response = await apiClient.post(`/ai-agents/${targetAgent}/run`, {
        message: currentInput,
        userRole,
        conversationHistory: messages.filter(m => !m.isStatus).slice(-5),
      });

      // v3.2.6: Remove the "Thinking..."/"Routing..." status message
      setMessages(prev => prev.filter(m => !m.isStatus));

      const assistantMessage: CopilotMessage = {
        role: 'assistant',
        content: response.data?.output?.response || response.data?.output?.answer || response.data?.output?.diagnosis || JSON.stringify(response.data?.output || {}).slice(0, 500),
        timestamp: new Date().toISOString(),
        agentCode: targetAgent,
        confidence: response.data?.output?.confidence || response.data?.output?.confidence_pct,
        sources: response.data?.output?.sources || response.data?.output?.relevant_papers,
      };
      setMessages(prev => [...prev, assistantMessage]);

      // v3.2.6: Auto-route to specialist if COPILOT suggested it
      if (targetAgent === 'COPILOT') {
        const suggestedAgents = response.data?.output?.suggested_agents || response.data?.output?.agent_routing;
        if (Array.isArray(suggestedAgents) && suggestedAgents.length > 0) {
          const firstSuggested = typeof suggestedAgents[0] === 'string'
            ? suggestedAgents[0]
            : suggestedAgents[0]?.code || suggestedAgents[0]?.agent;

          // Check if this agent is available in the user's roleAgents
          const availableAgents = agents.map(a => a.code);
          if (firstSuggested && availableAgents.includes(firstSuggested) && firstSuggested !== 'COPILOT') {
            // Show "Routing..." status
            setMessages(prev => [...prev, {
              role: 'system',
              content: `🔄 ${firstSuggested} specialist is handling your question...`,
              timestamp: new Date().toISOString(),
              isStatus: true,
            }]);

            // Auto-route to the specialist agent
            setActiveAgent(firstSuggested);
            try {
              const specialistResponse = await apiClient.post(`/ai-agents/${firstSuggested}/run`, {
                message: currentInput,
                userRole,
                conversationHistory: messages.filter(m => !m.isStatus).slice(-5),
              });

              // Remove "Routing..." status
              setMessages(prev => prev.filter(m => !m.isStatus));

              // Show specialist response + "Done" notification
              const specialistMessage: CopilotMessage = {
                role: 'assistant',
                content: specialistResponse.data?.output?.response || specialistResponse.data?.output?.answer || specialistResponse.data?.output?.diagnosis || JSON.stringify(specialistResponse.data?.output || {}).slice(0, 500),
                timestamp: new Date().toISOString(),
                agentCode: firstSuggested,
                confidence: specialistResponse.data?.output?.confidence || specialistResponse.data?.output?.confidence_pct,
                sources: specialistResponse.data?.output?.sources || specialistResponse.data?.output?.relevant_papers,
              };
              setMessages(prev => [...prev, specialistMessage]);

              // v3.2.6: "Done" notification
              setMessages(prev => [...prev, {
                role: 'system',
                content: `✅ ${firstSuggested} specialist completed. Switched back to Copilot.`,
                timestamp: new Date().toISOString(),
                isStatus: true,
              }]);
              // Switch back to COPILOT after auto-routing
              setActiveAgent('COPILOT');
              // Auto-remove the "Done" status after 4 seconds
              setTimeout(() => {
                setMessages(prev => prev.filter(m => !(m.isStatus && m.content.includes('completed'))));
              }, 4000);
            } catch (specialistErr: any) {
              setMessages(prev => prev.filter(m => !m.isStatus));
              const errMsg = specialistErr.response?.data?.message || specialistErr.response?.data?.error || specialistErr.message;
              setMessages(prev => [...prev, {
                role: 'assistant',
                content: `The ${firstSuggested} specialist encountered an error: ${errMsg}. Here's the Copilot's general response above — you can also try the ${firstSuggested} button directly.`,
                timestamp: new Date().toISOString(),
                agentCode: firstSuggested,
              }]);
              setActiveAgent('COPILOT');
            }
          } else if (firstSuggested) {
            // v3.2.6: Suggested agent not available for this role — tell the user
            setMessages(prev => [...prev, {
              role: 'system',
              content: `💡 The Copilot suggests routing to ${firstSuggested}, but that agent isn't available for your role (${userRole}). Ask your admin to add it.`,
              timestamp: new Date().toISOString(),
              isStatus: true,
            }]);
            setTimeout(() => {
              setMessages(prev => prev.filter(m => !m.isStatus || !m.content.includes('suggests routing')));
            }, 6000);
          }
        }
      }
    } catch (error: any) {
      // v3.2.6: Remove status messages on error
      setMessages(prev => prev.filter(m => !m.isStatus));
      // v3.2.3: handle 503 (AI service unavailable) with a helpful message
      const status = error.response?.status;
      const serverHint = error.response?.data?.hint;
      const serverMessage = error.response?.data?.message || error.response?.data?.error;
      let content: string;
      if (status === 503) {
        content = `⚠️ AI service temporarily unavailable.\n\n${serverMessage || 'All AI providers are rate-limited or your daily quota is exhausted.'}\n\n${serverHint || 'Configure additional FREE API keys in Admin > API Keys (Groq for text, HuggingFace for vision, DeepSeek for reasoning, Qwen for daily assistant) to spread load across providers.'}`;
      } else {
        content = `Sorry, I couldn't process that request. ${serverMessage || error.message || 'Please try again.'}`;
      }
      const errorMessage: CopilotMessage = {
        role: 'assistant',
        content,
        timestamp: new Date().toISOString(),
        agentCode: targetAgent,
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  // ════════════════════════════════════════════════════════════════
  // v3.2.6: Speech-to-Text (voice input) via Web Speech API
  // ════════════════════════════════════════════════════════════════
  // Uses the browser's native Web Speech API (no API key needed).
  // Falls back gracefully if the browser doesn't support it.
  // For higher accuracy with Cameroon languages, the admin can configure
  // Groq Whisper in the API Keys tab — the server-side /api/ai/stt endpoint
  // will be used instead (not yet implemented in this widget, but the
  // backend route is ready).
  const toggleVoiceInput = () => {
    if (isListening) {
      // Stop listening
      recognitionRef.current?.stop();
      setIsListening(false);
      return;
    }

    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      // v3.2.6: browser doesn't support Web Speech API — show a helpful message
      setMessages(prev => [...prev, {
        role: 'system',
        content: 'Voice input is not supported in this browser. Please use Chrome, Edge, or Safari. Alternatively, ask your admin to configure Groq Whisper for server-side speech-to-text.',
        timestamp: new Date().toISOString(),
        isStatus: true,
      }]);
      setTimeout(() => setMessages(prev => prev.filter(m => !m.isStatus)), 6000);
      return;
    }

    const recognition = new SpeechRecognition();
    recognitionRef.current = recognition;
    recognition.lang = language;
    recognition.continuous = true; // v3.11.49: continuous mode — don't stop on first pause
    recognition.interimResults = true; // v3.11.49: show real-time transcription
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      setIsListening(true);
      setMessages(prev => [...prev, {
        role: 'system',
        content: `Listening in ${LANGUAGES.find(l => l.code === language)?.label || language}... Speak now.`,
        timestamp: new Date().toISOString(),
        isStatus: true,
      }]);
    };

    recognition.onresult = (event: any) => {
      // v3.11.49: Handle interim + final results
      let finalTranscript = '';
      let interimTranscript = '';
      for (let i = 0; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }
      // Show interim results in the input field as user speaks
      if (interimTranscript) {
        setInput(finalTranscript + interimTranscript);
      }
      // When final result arrives, set the final transcript
      if (finalTranscript) {
        setInput(finalTranscript);
        setMessages(prev => prev.filter(m => !m.isStatus));
      }
    };

    recognition.onerror = (event: any) => {
      setIsListening(false);
      setMessages(prev => prev.filter(m => !m.isStatus));
      if (event.error === 'no-speech') {
        setMessages(prev => [...prev, {
          role: 'system',
          content: 'No speech detected. Please try again.',
          timestamp: new Date().toISOString(),
          isStatus: true,
        }]);
        setTimeout(() => setMessages(prev => prev.filter(m => !m.isStatus)), 3000);
      } else if (event.error === 'not-allowed') {
        setMessages(prev => [...prev, {
          role: 'system',
          content: 'Microphone access denied. Please allow microphone access in your browser settings.',
          timestamp: new Date().toISOString(),
          isStatus: true,
        }]);
        setTimeout(() => setMessages(prev => prev.filter(m => !m.isStatus)), 5000);
      }
    };

    recognition.onend = () => {
      setIsListening(false);
      setMessages(prev => prev.filter(m => !m.isStatus));
    };

    recognition.start();
  };

  // ════════════════════════════════════════════════════════════════
  // v3.2.6: Text-to-Speech (voice output) via Web Speech API
  // ════════════════════════════════════════════════════════════════
  // Uses the browser's native SpeechSynthesis API (no API key needed).
  // For higher-quality neural voices, the admin can configure Fish Audio
  // or Kokoro in the API Keys tab — the server-side /api/ai/tts endpoint
  // will be used instead (backend route ready, frontend wired to call it).
  const speakMessage = (text: string, messageIdx: number) => {
    // If already speaking this message, stop
    if (speakingMessageIdx === messageIdx) {
      window.speechSynthesis.cancel();
      setSpeakingMessageIdx(null);
      return;
    }

    // Cancel any ongoing speech
    window.speechSynthesis.cancel();

    // v3.2.6: Try server-side TTS first (Fish Audio / Kokoro) for better quality
    // If it fails, fall back to browser-native Web Speech API
    const useServerTTS = true; // v3.11.49: enabled
    if (useServerTTS) {
      apiClient.post('/ai/tts', { text, language, voice: 'alloy' })
        .then(res => {
          if (res.data?.audio_url) {
            const audio = new Audio(res.data.audio_url);
            setSpeakingMessageIdx(messageIdx);
            audio.onended = () => setSpeakingMessageIdx(null);
            audio.play();
          } else {
            throw new Error('No audio URL');
          }
        })
        .catch(() => {
          // Fall back to browser TTS
          browserSpeak(text, messageIdx);
        });
    } else {
      browserSpeak(text, messageIdx);
    }
  };

  const browserSpeak = (text: string, messageIdx: number) => {
    if (!('speechSynthesis' in window)) {
      setMessages(prev => [...prev, {
        role: 'system',
        content: 'Text-to-speech is not supported in this browser.',
        timestamp: new Date().toISOString(),
        isStatus: true,
      }]);
      setTimeout(() => setMessages(prev => prev.filter(m => !m.isStatus)), 3000);
      return;
    }

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = language;
    utterance.rate = 0.95;
    utterance.pitch = 1.0;

    // Try to find a matching voice for the selected language
    const voices = window.speechSynthesis.getVoices();
    const matchingVoice = voices.find(v => v.lang.startsWith(language.split('-')[0]));
    if (matchingVoice) {
      utterance.voice = matchingVoice;
    }

    utterance.onstart = () => setSpeakingMessageIdx(messageIdx);
    utterance.onend = () => setSpeakingMessageIdx(null);
    utterance.onerror = () => setSpeakingMessageIdx(null);

    window.speechSynthesis.speak(utterance);
  };

  const searchKnowledge = async () => {
    if (!input.trim()) return;
    setLoading(true);
    try {
      const response = await apiClient.get(`/knowledge/search/hybrid`, {
        params: { q: input, limit: 5 },
      });
      setKnowledgeResults(response.data?.results || []);
      setShowKnowledge(true);
    } catch (error: any) {
      setKnowledgeResults([]);
    } finally {
      setLoading(false);
    }
  };

  const driverLanguageOptions = [
    { code: 'en', label: 'English' },
    ...LANGUAGES.filter(item => item.code !== 'en-US').map(item => ({ code: item.code, label: item.label })),
  ];

  const agentIcon = (code: string) => code === 'LOGISTICS' ? <RouteIcon /> : <SparkIcon className="w-4 h-4" />;

  const financeAgentIcon = (code: string) => {
    const common = 'w-3.5 h-3.5';
    if (code === 'KNOWLEDGE') return <BookIcon />;
    if (code === 'COMPLIANCE' || code === 'TRANSLATION') return <GlobeIcon />;
    if (code === 'MARKET') return <svg className={common} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 18l5-5 4 3 8-9"/><path d="M16 7h4v4"/><path d="M3 21h18"/></svg>;
    if (code === 'FINANCE') return <svg className={common} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 6h14a2 2 0 012 2v10H4a2 2 0 01-2-2V6a2 2 0 012-2h12"/><path d="M15 11h5v4h-5a2 2 0 010-4z"/></svg>;
    if (code === 'EXECUTIVE') return <svg className={common} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 20V10M10 20V4M16 20v-7M22 20H2"/></svg>;
    if (code === 'RISK') return <svg className={common} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 3l8 3v5c0 5-3.4 8.5-8 10-4.6-1.5-8-5-8-10V6l8-3z"/><path d="M12 8v5M12 16h.01"/></svg>;
    return <SparkIcon className={common} />;
  };

  const financePromptSet = workspaceContext === 'payments'
    ? [
        ['Explain this payment queue', 'Help me understand a pending payment exception or review point without taking any action.'],
        ['Check payment procedure', 'Find the relevant finance procedure for reviewing and approving a batch payment.'],
      ]
    : workspaceContext === 'withdrawals'
      ? [
          ['Explain a withdrawal issue', 'Help me understand a withdrawal request or exception without approving or rejecting it.'],
          ['Review withdrawal procedure', 'Find the relevant guidance for reviewing a withdrawal request.'],
        ]
      : workspaceContext === 'transactions'
        ? [
            ['Explain transaction status', 'Explain how to interpret a transaction status or reference in the finance ledger.'],
            ['Review ledger exception', 'Help me reason through a transaction-ledger exception without changing any transaction.'],
          ]
        : workspaceContext === 'reports'
          ? [
              ['Explain reconciliation workflow', 'Explain how to use the available finance reports for reconciliation and audit review.'],
              ['Find export guidance', 'Find finance guidance for exports, reconciliation, or audit review.'],
            ]
          : workspaceContext === 'ai-insights'
            ? [
                ['Summarize AI finance insights', 'Explain how to interpret the current finance AI insights as advisory analysis only.'],
                ['Review market and risk context', 'Summarize market, finance, risk, or compliance context without taking operational action.'],
              ]
            : [
                ['Summarize pending finance work', 'Summarize the finance work that may need attention.'],
                ['Find finance guidance', 'Find finance, reconciliation, risk, or compliance guidance relevant to my current task.'],
              ];

  const fieldAgentPrompts = [
    'Help me register a farmer',
    'Explain a KYC issue',
    'How do I record a batch?',
    'What should I do with this harvest alert?',
  ];

  const farmerPrompts = [
    'What could be affecting my crop?',
    'What fertilizer should I consider?',
    'How can I improve my yield?',
    'What should I know about the weather?',
  ];

  const farmerPanel = open && isFarmer ? (
    <div className="ent-copilot-agent-overlay ent-copilot-farmer-overlay" role="presentation">
      <section
        ref={panelRef}
        className="ent-copilot-mobile ent-copilot-agent-panel ent-copilot-farmer-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="farmer-copilot-title"
      >
        <header className="ent-copilot-agent-header">
          <div className="ent-copilot-agent-brand">
            <span className="ent-copilot-agent-brand-icon"><SparkIcon /></span>
            <div>
              <strong id="farmer-copilot-title">COCONNECT COPILOT</strong>
              <small>Farmer Assistant</small>
            </div>
          </div>
          <button onClick={() => setOpen(false)} className="ent-copilot-agent-close" aria-label="Close Copilot">
            <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 6l12 12M6 18L18 6" /></svg>
          </button>
        </header>

        <div className="ent-copilot-agent-selector">
          <label htmlFor="farmer-copilot-agent">Assistant</label>
          <div className="ent-copilot-agent-select-wrap">
            <SparkIcon className="w-4 h-4" />
            <select id="farmer-copilot-agent" value={activeAgent} onChange={e => setActiveAgent(e.target.value)} aria-label="Farmer Copilot assistant">
              {agents.map(agent => <option key={agent.code} value={agent.code}>{agent.label}</option>)}
            </select>
            <ChevronDownIcon />
          </div>
        </div>

        <div ref={messagesScrollRef} className="ent-copilot-agent-messages" aria-live="polite">
          {messages.length === 0 && (
            <div className="ent-copilot-agent-welcome">
              <h3>How can I help with your farm?</h3>
              <p>Get help with crop disease, pests, fertilizer, yield, weather, harvest timing, agronomy, and farm knowledge.</p>
              <div className="ent-copilot-agent-prompts">
                {farmerPrompts.map(prompt => (
                  <button key={prompt} onClick={() => setInput(prompt)}>{prompt}</button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg, i) => (
            <div key={i} className={`ent-copilot-agent-message-row ${msg.role === 'user' ? 'is-user' : msg.isStatus ? 'is-status' : 'is-assistant'}`}>
              {msg.isStatus ? (
                <div className="ent-copilot-agent-status">
                  {loading && <span className="ent-copilot-agent-spinner" aria-hidden="true" />}
                  <span>{operationalStatusText(msg.content)}</span>
                </div>
              ) : (
                <div className={`ent-copilot-agent-bubble ${msg.role === 'user' ? 'is-user' : 'is-assistant'}`}>
                  {msg.role === 'assistant' && (
                    <div className="ent-copilot-agent-message-meta">
                      <span>{msg.agentCode && msg.agentCode !== 'COPILOT' ? msg.agentCode : 'COPILOT'}</span>
                      <button onClick={() => speakMessage(msg.content, i)} aria-label={speakingMessageIdx === i ? 'Stop reading response' : 'Read response aloud'}>
                        {speakingMessageIdx === i ? <SpeakerOffIcon /> : <SpeakerIcon />}
                      </button>
                    </div>
                  )}
                  <div className="ent-copilot-agent-message-content">{operationalStatusText(msg.content)}</div>
                  {msg.confidence != null && <div className="ent-copilot-agent-evidence">Confidence: {msg.confidence}%</div>}
                  {msg.sources && msg.sources.length > 0 && <div className="ent-copilot-agent-evidence">Sources: {msg.sources.length}</div>}
                </div>
              )}
            </div>
          ))}
          {loading && !messages.some(m => m.isStatus) && (
            <div className="ent-copilot-agent-message-row is-assistant"><div className="ent-copilot-agent-status"><span className="ent-copilot-agent-spinner" aria-hidden="true" /><span>Thinking</span></div></div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {showKnowledge && knowledgeResults.length > 0 && (
          <aside className="ent-copilot-agent-knowledge" aria-label="Knowledge results">
            <div><BookIcon /><strong>Knowledge results</strong><span>{knowledgeResults.length}</span></div>
            {knowledgeResults.slice(0, 3).map((r, i) => <p key={i}><strong>{r.type}: </strong>{r.title || r.chunk_text?.slice(0, 100) || 'Untitled'}</p>)}
            <button onClick={() => setShowKnowledge(false)}>Close results</button>
          </aside>
        )}

        <div className="ent-copilot-agent-secondary-toolbar">
          <label className="ent-copilot-agent-language">
            <GlobeIcon /><span className="sr-only">Language</span>
            <select value={language} onChange={e => setLanguage(e.target.value)} aria-label="Copilot language">
              {driverLanguageOptions.map(lang => <option key={lang.code} value={lang.code}>{lang.label}</option>)}
            </select>
            <ChevronDownIcon />
          </label>
          <button onClick={searchKnowledge} disabled={loading || !input.trim()} className="ent-copilot-agent-knowledge-button" title={!input.trim() ? 'Type a question to search knowledge' : 'Search knowledge'}>
            <BookIcon /><span>Knowledge</span>
          </button>
        </div>

        <footer className="ent-copilot-agent-composer">
          <button onClick={toggleVoiceInput} disabled={loading} className={isListening ? 'is-listening' : ''} aria-label={isListening ? 'Stop voice input' : 'Start voice input'}>
            {isListening ? <MicOffIcon /> : <MicIcon />}
          </button>
          <input type="text" value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), sendMessage())} placeholder={isListening ? `Listening in ${driverLanguageOptions.find(l => l.code === language)?.label || 'English'}...` : 'Ask Copilot...'} disabled={loading} aria-label="Ask Copilot" />
          <button onClick={() => sendMessage()} disabled={loading || !input.trim()} className="is-send" aria-label="Send message"><SendIcon /></button>
        </footer>
      </section>
    </div>
  ) : null;

  const fieldAgentPanel = open && isFieldAgent ? (
    <div className="ent-copilot-agent-overlay" role="presentation">
      <section
        ref={panelRef}
        className="ent-copilot-mobile ent-copilot-agent-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="field-agent-copilot-title"
      >
        <header className="ent-copilot-agent-header">
          <div className="ent-copilot-agent-brand">
            <span className="ent-copilot-agent-brand-icon"><SparkIcon /></span>
            <div>
              <strong id="field-agent-copilot-title">COCONNECT COPILOT</strong>
              <small>Field Operations Assistant</small>
            </div>
          </div>
          <button onClick={() => setOpen(false)} className="ent-copilot-agent-close" aria-label="Close Copilot">
            <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 6l12 12M6 18L18 6" /></svg>
          </button>
        </header>

        <div className="ent-copilot-agent-selector">
          <label htmlFor="field-agent-copilot-agent">Assistant</label>
          <div className="ent-copilot-agent-select-wrap">
            <SparkIcon className="w-4 h-4" />
            <select id="field-agent-copilot-agent" value={activeAgent} onChange={e => setActiveAgent(e.target.value)} aria-label="Field Agent Copilot assistant">
              {agents.map(agent => <option key={agent.code} value={agent.code}>{agent.label}</option>)}
            </select>
            <ChevronDownIcon />
          </div>
        </div>

        <div ref={messagesScrollRef} className="ent-copilot-agent-messages" aria-live="polite">
          {messages.length === 0 && (
            <div className="ent-copilot-agent-welcome">
              <h3>How can I help in the field?</h3>
              <p>Get help with farmer onboarding, KYC, batch recording, harvest alerts, farm mapping, and field advisory.</p>
              <div className="ent-copilot-agent-prompts">
                {fieldAgentPrompts.map(prompt => (
                  <button key={prompt} onClick={() => setInput(prompt)}>{prompt}</button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg, i) => (
            <div key={i} className={`ent-copilot-agent-message-row ${msg.role === 'user' ? 'is-user' : msg.isStatus ? 'is-status' : 'is-assistant'}`}>
              {msg.isStatus ? (
                <div className="ent-copilot-agent-status">
                  {loading && <span className="ent-copilot-agent-spinner" aria-hidden="true" />}
                  <span>{operationalStatusText(msg.content)}</span>
                </div>
              ) : (
                <div className={`ent-copilot-agent-bubble ${msg.role === 'user' ? 'is-user' : 'is-assistant'}`}>
                  {msg.role === 'assistant' && (
                    <div className="ent-copilot-agent-message-meta">
                      <span>{msg.agentCode && msg.agentCode !== 'COPILOT' ? msg.agentCode : 'COPILOT'}</span>
                      <button onClick={() => speakMessage(msg.content, i)} aria-label={speakingMessageIdx === i ? 'Stop reading response' : 'Read response aloud'}>
                        {speakingMessageIdx === i ? <SpeakerOffIcon /> : <SpeakerIcon />}
                      </button>
                    </div>
                  )}
                  <div className="ent-copilot-agent-message-content">{operationalStatusText(msg.content)}</div>
                  {msg.confidence != null && <div className="ent-copilot-agent-evidence">Confidence: {msg.confidence}%</div>}
                  {msg.sources && msg.sources.length > 0 && <div className="ent-copilot-agent-evidence">Sources: {msg.sources.length}</div>}
                </div>
              )}
            </div>
          ))}
          {loading && !messages.some(m => m.isStatus) && (
            <div className="ent-copilot-agent-message-row is-assistant"><div className="ent-copilot-agent-status"><span className="ent-copilot-agent-spinner" aria-hidden="true" /><span>Thinking</span></div></div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {showKnowledge && knowledgeResults.length > 0 && (
          <aside className="ent-copilot-agent-knowledge" aria-label="Knowledge results">
            <div><BookIcon /><strong>Knowledge results</strong><span>{knowledgeResults.length}</span></div>
            {knowledgeResults.slice(0, 3).map((r, i) => <p key={i}><strong>{r.type}: </strong>{r.title || r.chunk_text?.slice(0, 100) || 'Untitled'}</p>)}
            <button onClick={() => setShowKnowledge(false)}>Close results</button>
          </aside>
        )}

        <div className="ent-copilot-agent-secondary-toolbar">
          <label className="ent-copilot-agent-language">
            <GlobeIcon /><span className="sr-only">Language</span>
            <select value={language} onChange={e => setLanguage(e.target.value)} aria-label="Copilot language">
              {driverLanguageOptions.map(lang => <option key={lang.code} value={lang.code}>{lang.label}</option>)}
            </select>
            <ChevronDownIcon />
          </label>
          <button onClick={searchKnowledge} disabled={loading || !input.trim()} className="ent-copilot-agent-knowledge-button" title={!input.trim() ? 'Type a question to search knowledge' : 'Search knowledge'}>
            <BookIcon /><span>Knowledge</span>
          </button>
        </div>

        <footer className="ent-copilot-agent-composer">
          <button onClick={toggleVoiceInput} disabled={loading} className={isListening ? 'is-listening' : ''} aria-label={isListening ? 'Stop voice input' : 'Start voice input'}>
            {isListening ? <MicOffIcon /> : <MicIcon />}
          </button>
          <input type="text" value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), sendMessage())} placeholder={isListening ? `Listening in ${driverLanguageOptions.find(l => l.code === language)?.label || 'English'}...` : 'Ask Copilot...'} disabled={loading} aria-label="Ask Copilot" />
          <button onClick={() => sendMessage()} disabled={loading || !input.trim()} className="is-send" aria-label="Send message"><SendIcon /></button>
        </footer>
      </section>
    </div>
  ) : null;

  const driverPanel = open && isDriver ? (
    <div className="ent-copilot-driver-overlay" role="presentation">
      <section
        ref={panelRef}
        className="ent-copilot-mobile ent-copilot-driver-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="driver-copilot-title"
      >
        <header className="ent-copilot-driver-header">
          <div className="ent-copilot-driver-brand">
            <span className="ent-copilot-driver-brand-icon"><SparkIcon /></span>
            <div>
              <strong id="driver-copilot-title">COCONNECT COPILOT</strong>
              <small>Driver Assistant · Logistics aware</small>
            </div>
          </div>
          <button onClick={() => setOpen(false)} className="ent-copilot-driver-close" aria-label="Close Copilot">
            <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 6l12 12M6 18L18 6" /></svg>
          </button>
        </header>

        <div className="ent-copilot-driver-agents" role="tablist" aria-label="Copilot agent">
          {agents.map(agent => (
            <button
              key={agent.code}
              onClick={() => setActiveAgent(agent.code)}
              className={activeAgent === agent.code ? 'is-active' : ''}
              role="tab"
              aria-selected={activeAgent === agent.code}
            >
              {agentIcon(agent.code)}
              <span>{agent.label}</span>
            </button>
          ))}
        </div>

        <div ref={messagesScrollRef} className="ent-copilot-driver-messages" aria-live="polite">
          {messages.length === 0 && (
            <div className="ent-copilot-driver-welcome">
              <h3>How can I help with this trip?</h3>
              <p>Ask for help understanding a dispatch, route issue, GPS problem, or delivery workflow.</p>
              <div className="ent-copilot-driver-prompts">
                {[
                  'Explain my current dispatch',
                  'Help with a route issue',
                  'What should I do if GPS is unavailable?',
                  'Summarize this delivery',
                ].map(prompt => (
                  <button key={prompt} onClick={() => setInput(prompt)}>{prompt}</button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg, i) => (
            <div key={i} className={`ent-copilot-driver-message-row ${msg.role === 'user' ? 'is-user' : msg.isStatus ? 'is-status' : 'is-assistant'}`}>
              {msg.isStatus ? (
                <div className="ent-copilot-driver-status">
                  {loading && <span className="ent-copilot-driver-spinner" aria-hidden="true" />}
                  <span>{driverStatusText(msg.content)}</span>
                </div>
              ) : (
                <div className={`ent-copilot-driver-bubble ${msg.role === 'user' ? 'is-user' : 'is-assistant'}`}>
                  {msg.role === 'assistant' && (
                    <div className="ent-copilot-driver-message-meta">
                      <span>{msg.agentCode && msg.agentCode !== 'COPILOT' ? msg.agentCode : 'COPILOT'}</span>
                      <button
                        onClick={() => speakMessage(msg.content, i)}
                        aria-label={speakingMessageIdx === i ? 'Stop reading response' : 'Read response aloud'}
                        title={speakingMessageIdx === i ? 'Stop speaking' : 'Read aloud'}
                      >
                        {speakingMessageIdx === i ? <SpeakerOffIcon /> : <SpeakerIcon />}
                      </button>
                    </div>
                  )}
                  <div className="ent-copilot-driver-message-content">{driverStatusText(msg.content)}</div>
                  {msg.confidence != null && <div className="ent-copilot-driver-evidence">Confidence: {msg.confidence}%</div>}
                  {msg.sources && msg.sources.length > 0 && <div className="ent-copilot-driver-evidence">Sources: {msg.sources.length}</div>}
                </div>
              )}
            </div>
          ))}
          {loading && !messages.some(m => m.isStatus) && (
            <div className="ent-copilot-driver-message-row is-assistant">
              <div className="ent-copilot-driver-status"><span className="ent-copilot-driver-spinner" aria-hidden="true" /><span>Thinking</span></div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {showKnowledge && knowledgeResults.length > 0 && (
          <aside className="ent-copilot-driver-knowledge" aria-label="Knowledge results">
            <div><BookIcon /><strong>Knowledge results</strong><span>{knowledgeResults.length}</span></div>
            {knowledgeResults.slice(0, 3).map((r, i) => (
              <p key={i}><strong>{r.type}: </strong>{r.title || r.chunk_text?.slice(0, 100) || 'Untitled'}</p>
            ))}
            <button onClick={() => setShowKnowledge(false)}>Close results</button>
          </aside>
        )}

        <div className="ent-copilot-driver-secondary-toolbar">
          <label className="ent-copilot-driver-language">
            <GlobeIcon />
            <span className="sr-only">Language</span>
            <select value={language} onChange={e => setLanguage(e.target.value)} aria-label="Copilot language">
              {driverLanguageOptions.map(lang => <option key={lang.code} value={lang.code}>{lang.label}</option>)}
            </select>
            <ChevronDownIcon />
          </label>
          <button
            onClick={searchKnowledge}
            disabled={loading || !input.trim()}
            className="ent-copilot-driver-knowledge-button"
            title={!input.trim() ? 'Type a question to search knowledge' : 'Search knowledge'}
          >
            <BookIcon /><span>Knowledge</span>
          </button>
        </div>

        <footer className="ent-copilot-driver-composer">
          <button
            onClick={toggleVoiceInput}
            disabled={loading}
            className={isListening ? 'is-listening' : ''}
            aria-label={isListening ? 'Stop voice input' : 'Start voice input'}
            title={isListening ? 'Stop listening' : 'Voice input'}
          >
            {isListening ? <MicOffIcon /> : <MicIcon />}
          </button>
          <input
            type="text"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), sendMessage())}
            placeholder={isListening ? `Listening in ${driverLanguageOptions.find(l => l.code === language)?.label || 'English'}...` : 'Ask Copilot...'}
            disabled={loading}
            aria-label="Ask Copilot"
          />
          <button onClick={() => sendMessage()} disabled={loading || !input.trim()} className="is-send" aria-label="Send message">
            <SendIcon />
          </button>
        </footer>
      </section>
    </div>
  ) : null;

  return (
    <>
      {!open && (
        <button
          ref={fabRef}
          onClick={() => setOpen(true)}
          className={`copilot-fab ${isDriver ? 'copilot-fab--driver' : isFieldAgent ? 'copilot-fab--agent' : isFarmer ? 'copilot-fab--farmer' : isAdmin ? 'copilot-fab--admin' : isFactoryClerk ? 'copilot-fab--clerk' : isFinance ? 'copilot-fab--finance' : ''} w-14 h-14 rounded-full bg-cargo-600 hover:bg-cargo-700 text-white shadow-lg flex items-center justify-center transition-all hover:scale-110`}
          aria-label="Open AI Copilot"
        >
          {(isOperationalMobileCopilot || isAdmin || isFactoryClerk || isFinance) ? <SparkIcon className="w-5 h-5" /> : (
            <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2z" /><path d="M8 14s1.5 2 4 2 4-2 4-2" /><line x1="9" y1="9" x2="9.01" y2="9" /><line x1="15" y1="9" x2="15.01" y2="9" /></svg>
          )}
        </button>
      )}

      {/* Chat panel */}
      {open && !isDriver && !isFieldAgent && !isFarmer && (
        <div className={`fixed bottom-6 right-6 z-50 w-96 max-w-[calc(100vw-3rem)] h-[32rem] max-h-[calc(100vh-3rem)] ent-copilot-mobile ${isAdmin ? 'ent-copilot-admin' : isFactoryClerk ? 'ent-copilot-clerk' : isFinance ? 'ent-copilot-finance' : ''} bg-ink-0 dark:bg-ink-900 rounded-2xl shadow-2xl border border-ink-200 dark:border-ink-700 flex flex-col overflow-hidden`}>
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b border-ink-200 dark:border-ink-700 bg-cargo-600 text-white">
            <div className="flex items-center gap-2">
              {(isAdmin || isFactoryClerk || isFinance) ? <SparkIcon className="w-5 h-5" /> : (
                <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2z" />
                  <path d="M8 14s1.5 2 4 2 4-2 4-2" />
                  <line x1="9" y1="9" x2="9.01" y2="9" />
                  <line x1="15" y1="9" x2="15.01" y2="9" />
                </svg>
              )}
              <span className="font-semibold text-sm">{(isAdmin || isFactoryClerk || isFinance) ? 'COCONNECT COPILOT' : 'CoConnect AI Copilot'}{isAdmin && <span className="admin-copilot-subtitle">Admin Assistant</span>}{isFactoryClerk && <span className="clerk-copilot-subtitle">Factory Operations Assistant</span>}{isFinance && <span className="finance-copilot-subtitle">Finance Operations Assistant</span>}</span>
            </div>
            <button onClick={() => setOpen(false)} className="hover:bg-white/20 rounded p-1">
              <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 6l12 12M6 18L18 6" />
              </svg>
            </button>
          </div>

          {/* Agent selector */}
          <div className="flex gap-1 p-2 border-b border-ink-200 dark:border-ink-700 overflow-x-auto no-scrollbar bg-ink-50 dark:bg-ink-850">
            {agents.map(agent => (
              <button
                key={agent.code}
                onClick={() => setActiveAgent(agent.code)}
                className={`shrink-0 px-2.5 py-1 rounded-lg text-2xs font-medium transition-colors ${
                  activeAgent === agent.code
                    ? 'bg-cargo-600 text-white'
                    : 'bg-ink-100 dark:bg-ink-800 text-ink-600 dark:text-ink-400 hover:bg-ink-200 dark:hover:bg-ink-700'
                }`}
              >
                {isFinance ? <><span className="finance-copilot-agent-icon">{financeAgentIcon(agent.code)}</span><span>{agent.label}</span></> : (isAdmin || isFactoryClerk) ? agent.label : <>{agent.icon} {agent.label}</>}
              </button>
            ))}
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-3 space-y-3 bg-ink-50 dark:bg-ink-950">
            {messages.length === 0 && (
              <div className="text-center py-8">
                {isAdmin ? (
                  <>
                    <p className="text-sm font-medium text-ink-700 dark:text-ink-200">How can I help with this workspace?</p>
                    <p className="text-2xs text-ink-400 dark:text-ink-500 mt-1">Ask about operations, compliance, system health, risk, or platform data.</p>
                    <div className="mt-4 space-y-1.5">
                      <button onClick={() => setInput('Summarize the current operational status.')} className="block w-full text-left text-2xs px-3 py-2 rounded-lg bg-ink-100 dark:bg-ink-800 text-ink-600 dark:text-ink-400 hover:bg-ink-200 dark:hover:bg-ink-700">Summarize the current operational status</button>
                      <button onClick={() => setInput('What compliance or risk issues need attention?')} className="block w-full text-left text-2xs px-3 py-2 rounded-lg bg-ink-100 dark:bg-ink-800 text-ink-600 dark:text-ink-400 hover:bg-ink-200 dark:hover:bg-ink-700">Review compliance and risk issues</button>
                      <button onClick={() => setInput('Explain the current Admin module and its key controls.')} className="block w-full text-left text-2xs px-3 py-2 rounded-lg bg-ink-100 dark:bg-ink-800 text-ink-600 dark:text-ink-400 hover:bg-ink-200 dark:hover:bg-ink-700">Explain this Admin workspace</button>
                    </div>
                  </>
                ) : isFactoryClerk ? (
                  <>
                    <p className="text-sm font-medium text-ink-700 dark:text-ink-200">How can I help with factory operations?</p>
                    <p className="text-2xs text-ink-400 dark:text-ink-500 mt-1">Use Copilot for intake context, routing, variance review, warehouse coordination, and operating guidance. Final quality and routing decisions remain with authorized workflows.</p>
                    <div className="mt-4 space-y-1.5 clerk-copilot-prompts">
                      <button onClick={() => setInput('Summarize what I should review in the pending intake queue.')} className="block w-full text-left text-2xs px-3 py-2 rounded-lg bg-ink-100 dark:bg-ink-800 text-ink-600 dark:text-ink-400 hover:bg-ink-200 dark:hover:bg-ink-700">Summarize pending intake priorities</button>
                      <button onClick={() => setInput('Help me interpret a weight variance at intake.')} className="block w-full text-left text-2xs px-3 py-2 rounded-lg bg-ink-100 dark:bg-ink-800 text-ink-600 dark:text-ink-400 hover:bg-ink-200 dark:hover:bg-ink-700">Interpret a weight variance</button>
                      <button onClick={() => setInput('Explain the available batch routing options and when each is used.')} className="block w-full text-left text-2xs px-3 py-2 rounded-lg bg-ink-100 dark:bg-ink-800 text-ink-600 dark:text-ink-400 hover:bg-ink-200 dark:hover:bg-ink-700">Explain routing options</button>
                      <button onClick={() => setInput('Find operating guidance for sensory QC or lab-test handling.')} className="block w-full text-left text-2xs px-3 py-2 rounded-lg bg-ink-100 dark:bg-ink-800 text-ink-600 dark:text-ink-400 hover:bg-ink-200 dark:hover:bg-ink-700">Find QC and lab guidance</button>
                    </div>
                  </>
                ) : isFinance ? (
                  <>
                    <p className="text-sm font-medium text-ink-700 dark:text-ink-200">How can I help with finance operations?</p>
                    <p className="text-2xs text-ink-400 dark:text-ink-500 mt-1">Use Copilot to understand payment and withdrawal exceptions, transaction status, reports, market context, risk, compliance, and finance procedures. Copilot does not approve or disburse funds.</p>
                    <div className="mt-4 space-y-1.5 finance-copilot-prompts">
                      {financePromptSet.map(([label, prompt]) => (
                        <button key={label} onClick={() => setInput(prompt)} className="block w-full text-left text-2xs px-3 py-2 rounded-lg bg-ink-100 dark:bg-ink-800 text-ink-600 dark:text-ink-400 hover:bg-ink-200 dark:hover:bg-ink-700">{label}</button>
                      ))}
                      <button onClick={() => setInput('Explain what risk or compliance guidance applies to the finance task I am reviewing.')} className="block w-full text-left text-2xs px-3 py-2 rounded-lg bg-ink-100 dark:bg-ink-800 text-ink-600 dark:text-ink-400 hover:bg-ink-200 dark:hover:bg-ink-700">Review risk & compliance guidance</button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-ink-500 dark:text-ink-400">👋 Hi! I'm your CoConnect AI Copilot.</p>
                    <p className="text-2xs text-ink-400 dark:text-ink-500 mt-1">Ask me about diseases, weather, market prices, farming advice, or anything about the CoConnect platform.</p>
                    <div className="mt-4 space-y-1.5">
                      <button onClick={() => setInput('What diseases are common in Cameroon cocoa?')} className="block w-full text-left text-2xs px-3 py-2 rounded-lg bg-ink-100 dark:bg-ink-800 text-ink-600 dark:text-ink-400 hover:bg-ink-200 dark:hover:bg-ink-700">🔬 What diseases are common in Cameroon cocoa?</button>
                      <button onClick={() => setInput('What is the current cocoa market price?')} className="block w-full text-left text-2xs px-3 py-2 rounded-lg bg-ink-100 dark:bg-ink-800 text-ink-600 dark:text-ink-400 hover:bg-ink-200 dark:hover:bg-ink-700">📈 What is the current cocoa market price?</button>
                      <button onClick={() => setInput('How do I register a new farmer?')} className="block w-full text-left text-2xs px-3 py-2 rounded-lg bg-ink-100 dark:bg-ink-800 text-ink-600 dark:text-ink-400 hover:bg-ink-200 dark:hover:bg-ink-700">📝 How do I register a new farmer?</button>
                    </div>
                  </>
                )}
              </div>
            )}
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : msg.isStatus ? 'justify-center' : 'justify-start'}`}>
                {msg.isStatus ? (
                  // v3.2.6: Status messages (Thinking, Routing, Done) — centered + muted
                  <div className="text-2xs text-ink-500 dark:text-ink-400 bg-ink-100 dark:bg-ink-800 rounded-full px-3 py-1 animate-pulse">
                    {msg.content}
                  </div>
                ) : (
                  <div className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${
                    msg.role === 'user'
                      ? 'bg-cargo-600 text-white'
                      : 'bg-ink-0 dark:bg-ink-800 text-ink-900 dark:text-ink-50 border border-ink-200 dark:border-ink-700'
                  }`}>
                    {msg.agentCode && msg.agentCode !== 'COPILOT' && (
                      <div className="text-2xs font-semibold opacity-70 mb-1 flex items-center justify-between">
                        <span>{msg.agentCode}</span>
                        {/* v3.2.6: TTS speaker button on AI responses */}
                        {msg.role === 'assistant' && (
                          <button
                            onClick={() => speakMessage(msg.content, i)}
                            className="ml-2 opacity-60 hover:opacity-100"
                            title={speakingMessageIdx === i ? 'Stop speaking' : 'Read aloud'}
                          >
                            {speakingMessageIdx === i ? <SpeakerOffIcon /> : <SpeakerIcon />}
                          </button>
                        )}
                      </div>
                    )}
                    {/* v3.2.6: TTS button for COPILOT responses (no agentCode header) */}
                    {msg.role === 'assistant' && msg.agentCode === 'COPILOT' && (
                      <div className="flex justify-end mb-1">
                        <button
                          onClick={() => speakMessage(msg.content, i)}
                          className="text-2xs opacity-60 hover:opacity-100"
                          title={speakingMessageIdx === i ? 'Stop speaking' : 'Read aloud'}
                        >
                          {speakingMessageIdx === i ? <><SpeakerOffIcon /> Stop</> : <><SpeakerIcon /> Read aloud</>}
                        </button>
                      </div>
                    )}
                    <div className="whitespace-pre-wrap">{msg.content}</div>
                    {msg.confidence != null && (
                      <div className="text-2xs opacity-60 mt-1">Confidence: {msg.confidence}%</div>
                    )}
                    {msg.sources && msg.sources.length > 0 && (
                      <div className="text-2xs opacity-60 mt-1">Sources: {msg.sources.length}</div>
                    )}
                  </div>
                )}
              </div>
            ))}
            {loading && !messages.some(m => m.isStatus) && (
              <div className="flex justify-start">
                <div className="bg-ink-0 dark:bg-ink-800 rounded-xl px-3 py-2 text-sm border border-ink-200 dark:border-ink-700">
                  <svg className="w-4 h-4 animate-spin text-ink-400" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" opacity="0.2" /><path d="M12 2a10 10 0 0110 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Knowledge search results */}
          {showKnowledge && knowledgeResults.length > 0 && (
            <div className="border-t border-ink-200 dark:border-ink-700 p-2 max-h-32 overflow-y-auto bg-ink-50 dark:bg-ink-850">
              <div className="text-2xs font-medium text-ink-500 mb-1"><BookIcon /> Knowledge Results ({knowledgeResults.length})</div>
              {knowledgeResults.slice(0, 3).map((r, i) => (
                <div key={i} className="text-2xs text-ink-600 dark:text-ink-400 py-1 border-b border-ink-100 dark:border-ink-800 last:border-0">
                  <span className="font-medium">{r.type}: </span>
                  {r.title || r.chunk_text?.slice(0, 100) || 'Untitled'}
                </div>
              ))}
              <button onClick={() => setShowKnowledge(false)} className="text-2xs text-cargo-600 hover:underline mt-1">Close</button>
            </div>
          )}

          {/* v3.2.6: Language selector (compact) */}
          <div className="px-2 py-1 border-t border-ink-200 dark:border-ink-700 bg-ink-50 dark:bg-ink-850 flex items-center gap-1 overflow-x-auto no-scrollbar">
            <span className="text-2xs text-ink-500 shrink-0 flex items-center"><GlobeIcon /></span>
            {LANGUAGES.map(lang => (
              <button
                key={lang.code}
                onClick={() => setLanguage(lang.code)}
                className={`shrink-0 px-1.5 py-0.5 rounded text-2xs ${
                  language === lang.code
                    ? 'bg-cargo-600 text-white'
                    : 'bg-ink-100 dark:bg-ink-800 text-ink-500 hover:bg-ink-200 dark:hover:bg-ink-700'
                }`}
                title={lang.label}
              >
                {lang.label}
              </button>
            ))}
          </div>

          {/* Input */}
          <div className="p-2 border-t border-ink-200 dark:border-ink-700 bg-ink-0 dark:bg-ink-900">
            <div className="flex gap-1.5 items-center">
              {/* v3.2.6: Voice input button (STT) */}
              <button
                onClick={toggleVoiceInput}
                disabled={loading}
                className={`px-2 py-2 rounded-lg text-sm ${
                  isListening
                    ? 'bg-red-500 text-white animate-pulse'
                    : 'bg-ink-100 dark:bg-ink-800 text-ink-600 dark:text-ink-400 hover:bg-ink-200 dark:hover:bg-ink-700'
                }`}
                title={isListening ? 'Stop listening' : 'Voice input'}
              >
                {isListening ? <MicOffIcon /> : <MicIcon />}
              </button>
              <input
                type="text"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), sendMessage())}
                placeholder={isListening ? `Listening in ${LANGUAGES.find(l => l.code === language)?.label}...` : `Ask ${activeAgent}...`}
                className="flex-1 px-3 py-2 rounded-lg text-sm bg-ink-100 dark:bg-ink-800 text-ink-900 dark:text-ink-50 border border-ink-200 dark:border-ink-700 focus:outline-none focus:ring-2 focus:ring-cargo-500"
                disabled={loading}
              />
              <button
                onClick={searchKnowledge}
                disabled={loading || !input.trim()}
                className="px-2 py-2 rounded-lg text-sm bg-ink-100 dark:bg-ink-800 text-ink-600 dark:text-ink-400 hover:bg-ink-200 dark:hover:bg-ink-700 disabled:opacity-50"
                title="Search knowledge base"
              >
                <BookIcon />
              </button>
              <button
                onClick={() => sendMessage()}
                disabled={loading || !input.trim()}
                className="px-3 py-2 rounded-lg text-sm bg-cargo-600 text-white hover:bg-cargo-700 disabled:opacity-50"
              >
                <SendIcon />
              </button>
            </div>
          </div>
        </div>
      )}


      {fieldAgentPanel && typeof document !== 'undefined' ? createPortal(fieldAgentPanel, document.body) : fieldAgentPanel}
      {farmerPanel && typeof document !== 'undefined' ? createPortal(farmerPanel, document.body) : farmerPanel}
      {driverPanel && typeof document !== 'undefined' ? createPortal(driverPanel, document.body) : driverPanel}
    </>
  );
};

export default CopilotWidget;
